import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, Phone, Building2, MapPin, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import BackLink from "@/components/BackLink";
import { brand } from "@/lib/site";
import { api } from "@/lib/api";

const HERO_IMG = "/login-hero.png";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";
const fieldClass =
    "h-11 border-[#E8E4DE] bg-[#FFFEF9] text-sm font-medium text-[#2D2926] shadow-sm placeholder:text-[#9A9088] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";
const fieldIconClass = `${fieldClass} pl-11`;
const labelClass = "text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]";

function formatErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(", ");
    return d.msg || JSON.stringify(d);
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
            <span className="px-2.5 text-[10px] leading-none text-[#C9A227]">◆</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <div className="space-y-2">
            <Label className={labelClass}>{label}</Label>
            <div className="relative">
                {Icon && (
                    <Icon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                )}
                {children}
            </div>
        </div>
    );
}

export default function Register() {
    const [searchParams] = useSearchParams();
    const selectedPlan = searchParams.get("plan") || "";
    const selectedCycle = searchParams.get("cycle") === "yearly" ? "yearly" : "monthly";
    const [publicPlans, setPublicPlans] = useState([]);
    const [form, setForm] = useState({
        business_name: "",
        full_name: "",
        email: "",
        password: "",
        mobile: "",
        city: "",
        business_type: "salon",
        plan_slug: selectedPlan,
    });
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/public/plans")
            .then((r) => setPublicPlans(r.data?.plans || []))
            .catch(() => setPublicPlans([]));
    }, []);

    const matchedPlan = publicPlans.find((p) => p.slug === selectedPlan);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await register(form);
            toast.success(`Welcome to ${brand.name}`);
            if (selectedPlan) {
                navigate("/app/billing", { state: { fromRegister: true, planSlug: selectedPlan, cycle: selectedCycle } });
            } else {
                navigate("/app/dashboard");
            }
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif] lg:grid lg:grid-cols-2">
            {/* Left — hero image (matches login) */}
            <div className="relative h-[32vh] min-h-[220px] lg:sticky lg:top-0 lg:h-screen lg:min-h-0">
                <img
                    src={HERO_IMG}
                    alt="Salon model with flowing hair"
                    className="absolute inset-0 h-full w-full object-cover object-[72%_center]"
                />
                <div
                    className="absolute inset-0 lg:hidden"
                    style={{
                        background:
                            "linear-gradient(to bottom, transparent 40%, rgba(250,249,246,0.85) 92%, #FAF9F6 100%)",
                    }}
                    aria-hidden
                />
                <div
                    className="absolute inset-y-0 right-0 hidden w-20 lg:block"
                    style={{
                        background: "linear-gradient(to right, transparent, rgba(250,249,246,0.65))",
                    }}
                    aria-hidden
                />
                <div
                    className="absolute inset-x-0 bottom-0 hidden h-[52%] lg:block"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(45,8,15,0.88) 0%, rgba(45,8,15,0.5) 42%, transparent 100%)",
                    }}
                    aria-hidden
                />

                <div className="relative hidden h-full flex-col p-10 xl:p-14 lg:flex">
                    <Link
                        to="/"
                        className="w-fit rounded-lg bg-[#FAF9F6]/95 px-4 py-2.5 shadow-[0_2px_20px_rgba(45,41,38,0.1)] backdrop-blur-sm transition-opacity hover:opacity-90"
                    >
                        <BrandLogo
                            size={34}
                            textClass="font-serif text-xl font-semibold text-[#4A0E17]"
                            accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                        />
                    </Link>

                    <div className="mt-auto max-w-[22rem]">
                        <div className="mb-4 h-px w-10 bg-[#D4AF37]" aria-hidden />
                        <p
                            className={`${EYEBROW} text-[#D4AF37]`}
                            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
                        >
                            14-day free trial
                        </p>
                        <p
                            className="mt-3 font-serif text-[1.65rem] font-normal leading-[1.35] tracking-tight text-white xl:text-[1.85rem]"
                            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
                        >
                            <span className="font-medium text-[#E8C96A]">14 days</span>, on the house
                            — bookings, GST billing &amp; AI, no card required.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right — register form */}
            <div className="relative flex flex-1 justify-center px-6 py-10 sm:px-10 lg:py-12">
                <div
                    className="pointer-events-none absolute inset-0 opacity-50"
                    style={{
                        background:
                            "radial-gradient(ellipse 70% 60% at 80% 20%, rgba(212,175,55,0.08), transparent)",
                    }}
                    aria-hidden
                />

                <div className="relative w-full max-w-[440px]">
                    <BackLink className="mb-5" data-testid="register-back-home" />

                    <div className="mb-7 lg:mb-8">
                        <Link to="/" className="inline-block transition-opacity hover:opacity-90 lg:hidden">
                            <BrandLogo
                                size={36}
                                textClass="font-serif text-2xl font-semibold text-[#4A0E17]"
                                accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                            />
                        </Link>
                        <div className="hidden lg:block">
                            <p className={EYEBROW} style={{ color: "#C9A227" }}>
                                Get started
                            </p>
                            <h1 className="mt-2 font-serif text-[2.125rem] font-semibold leading-tight tracking-tight text-[#4A0E17]">
                                Open your salon
                            </h1>
                            <GoldDivider className="mt-4" />
                        </div>
                        {matchedPlan && (
                            <p className="mt-4 hidden rounded-lg border border-[#FDF3DC] bg-[#FFFBF5] px-3 py-2 text-sm text-[#9A7B10] lg:block">
                                <strong>{matchedPlan.name}</strong> plan · {selectedCycle === "yearly" ? "Yearly" : "Monthly"} after trial
                            </p>
                        )}
                    </div>

                    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                        <div className="text-center lg:hidden">
                            <h1 className="font-serif text-3xl font-semibold tracking-tight text-[#4A0E17]">
                                Open your salon
                            </h1>
                            <GoldDivider className="mx-auto mt-4" />
                            {matchedPlan && (
                                <p className="mt-3 rounded-lg border border-[#FDF3DC] bg-[#FFFBF5] px-3 py-2 text-sm text-[#9A7B10]">
                                    <strong>{matchedPlan.name}</strong> plan · {selectedCycle === "yearly" ? "Yearly" : "Monthly"} billing after trial
                                </p>
                            )}
                            {!matchedPlan && selectedPlan && (
                                <p className="mt-3 text-sm font-semibold text-[#9A7B10]">Plan: {selectedPlan}</p>
                            )}
                            <p className="mt-3 text-sm font-medium text-[#8A8178]">
                                14-day free trial · No credit card
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <Field label="Business name" icon={Building2}>
                                    <Input
                                        value={form.business_name}
                                        onChange={set("business_name")}
                                        required
                                        className={fieldIconClass}
                                        placeholder="Tress & Tonic Salon"
                                        data-testid="reg-business-name"
                                    />
                                </Field>
                            </div>

                            <Field label="Your name" icon={User}>
                                <Input
                                    value={form.full_name}
                                    onChange={set("full_name")}
                                    required
                                    className={fieldIconClass}
                                    placeholder="Your full name"
                                    data-testid="reg-full-name"
                                />
                            </Field>

                            <Field label="Mobile" icon={Phone}>
                                <Input
                                    value={form.mobile}
                                    onChange={set("mobile")}
                                    required
                                    placeholder="+91 98765 43210"
                                    className={fieldIconClass}
                                    data-testid="reg-mobile"
                                />
                            </Field>

                            <div className="sm:col-span-2">
                                <Field label="Email" icon={Mail}>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                        className={fieldIconClass}
                                        placeholder="you@yoursalon.com"
                                        data-testid="reg-email"
                                    />
                                </Field>
                            </div>

                            <Field label="Password" icon={Lock}>
                                <Input
                                    type={showPw ? "text" : "password"}
                                    value={form.password}
                                    onChange={set("password")}
                                    required
                                    minLength={8}
                                    className={`${fieldIconClass} pr-12`}
                                    placeholder="Min. 8 characters"
                                    data-testid="reg-password"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#8A8178] transition-colors hover:text-[#4A0E17]"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                </button>
                            </Field>

                            <Field label="City" icon={MapPin}>
                                <Input
                                    value={form.city}
                                    onChange={set("city")}
                                    className={fieldIconClass}
                                    placeholder="Mumbai"
                                    data-testid="reg-city"
                                />
                            </Field>

                            <div className="space-y-2 sm:col-span-2">
                                <Label className={labelClass}>Business type</Label>
                                <Select
                                    value={form.business_type}
                                    onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}
                                >
                                    <SelectTrigger
                                        className={`${fieldClass} h-11`}
                                        data-testid="reg-business-type"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="salon">Salon / Barbershop</SelectItem>
                                        <SelectItem value="spa">Spa / Wellness</SelectItem>
                                        <SelectItem value="both">Both</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={busy}
                            className="h-12 w-full rounded-lg bg-[#4A0E17] text-sm font-semibold tracking-wide shadow-[0_6px_24px_rgba(74,14,23,0.25)] transition-all hover:bg-[#3a0b12] hover:shadow-[0_8px_28px_rgba(74,14,23,0.32)]"
                            data-testid="register-submit-button"
                        >
                            {busy ? "Creating…" : "Start free trial"}
                        </Button>

                        <p className="text-center text-sm font-medium text-[#8A8178]">
                            Already with us?{" "}
                            <Link
                                to="/login"
                                className="font-semibold text-[#C84B31] underline-offset-4 hover:underline"
                            >
                                Sign in
                            </Link>
                        </p>
                    </form>

                    <p className="mt-8 text-center text-[11px] font-medium tracking-wide text-[#9A9088]">
                        Secured · Private to your salon
                    </p>
                </div>
            </div>
        </div>
    );
}
